import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import posthog from "posthog-js";
import { memo, useCallback, useRef, useState } from "react";
import { api } from "../api";
import Banner from "./tailwind/banner";
import { Button } from "./tailwind/button";
import { Dialog, DialogBody, DialogTitle } from "./tailwind/dialog";
import { Field, Label } from "./tailwind/fieldset";
import { Heading } from "./tailwind/heading";
import { Input } from "./tailwind/input";
import { Link } from "./tailwind/link";

// Extracted components for better organization
const BackButton = memo(({ onClick, text }) => (
  <Link
    className="hover:cursor-pointer hover:underline text-gray-500 hover:text-gray-700 flex mb-2"
    onClick={onClick}
  >
    <ArrowLeftIcon className="h-5 w-5" />
    {text}
  </Link>
));
BackButton.displayName = "BackButton";

const CodeInput = memo(
  ({ code, setCode, inputs, isCodeCorrect, handleKeyDown }) => (
    <div className="flex justify-between gap-2 my-3">
      {code.map((digit, index) => (
        <Input
          key={index}
          ref={(el) => (inputs.current[index] = el)}
          type="text"
          value={digit}
          maxLength="1"
          onPaste={(e) => {
            e.preventDefault();
            const pastedData = e.clipboardData.getData("text").trim();
            if (/^\d{6}$/.test(pastedData)) {
              setCode(pastedData.split(""));
            }
          }}
          onChange={(e) => {
            const newCode = [...code];
            newCode[index] = e.target.value.replace(/[^0-9]/g, "").slice(0, 1);
            setCode(newCode);
            if (newCode[index] && index < inputs.current.length - 1) {
              inputs.current[index + 1].focus();
            }
          }}
          onKeyDown={(e) => handleKeyDown(e, index)}
          style={{ textAlign: "center", aspectRatio: "1/1" }}
          autoComplete="off"
          invalid={!isCodeCorrect}
        />
      ))}
    </div>
  ),
);
CodeInput.displayName = "CodeInput";

export const WhatsappTwoFactorAuthentication = memo(({ data, setData }) => {
  if (!data) return null;

  const { email, password, id, phoneVerified, phone } = data;

  // State management
  const [shouldCloseModal, setShouldCloseModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFirstCodeSent, setIsFirstCodeSent] = useState(false);
  const [isUserPhoneSet, setIsUserPhoneSet] = useState(phoneVerified && phone);
  const [userPhone, setUserPhone] = useState(
    phoneVerified && phone ? phone : "",
  );
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [isCodeCorrect, setIsCodeCorrect] = useState(true);

  const inputs = useRef([]);
  const isVerified = phoneVerified ?? false;

  // Memoized callbacks
  const handleBackToLogin = useCallback(() => {
    setData(null);
    posthog.reset();
  }, [setData]);

  const handleResendCode = useCallback(() => {
    setIsFirstCodeSent(true);
    api.user.sendWhatsappVerificationCode(id);
    setCode(["", "", "", "", "", ""]);
  }, [id]);

  const handleLogin = useCallback(async () => {
    setIsLoading(true);
    const whatsappVerificationCode = code.join("");
    try {
      const result = await api.user.emailSignIn(id, {
        email,
        password,
        whatsappVerificationCode,
      });
      if (result.phoneVerified) {
        setIsCodeCorrect(true);
        setShouldCloseModal(true);
      }
    } catch {
      setIsCodeCorrect(false);
    }
    setIsLoading(false);
  }, [code, email, id, password]);

  const handleKeyDown = useCallback(
    (e, index) => {
      if (e.key === "Backspace" && !code[index] && index > 0) {
        inputs.current[index - 1].focus();
      }
    },
    [code],
  );

  const handlePhoneSubmit = useCallback(async () => {
    setIsUserPhoneSet(true);
    await api.user.sendWhatsappVerificationCode(id, { phone: userPhone });
    setIsFirstCodeSent(true);
  }, [id, userPhone]);

  return (
    <Dialog
      open={!shouldCloseModal}
      className="h-[100vh] w-[100vw] flex flex-col m-0"
      style={{ zIndex: "999999999" }}
      size="full-to-border"
    >
      <DialogTitle close={false} />
      <DialogBody className="h-full">
        <div className="flex flex-col h-full items-center justify-center w-full">
          {!isCodeCorrect && (
            <div className="w-1/3 my-5">
              <Banner
                style={{
                  whiteSpace: "pre-wrap",
                  overflowWrap: "break-word",
                  wordWrap: "break-word",
                }}
                title="Wrong code"
                content="Please enter the code your received on WhatsApp or ask to receive a new one."
              />
            </div>
          )}

          {!isUserPhoneSet ? (
            <Field className="w-1/4 min-w-96">
              <BackButton onClick={handleBackToLogin} text="Back to login" />
              <Heading className="text-xl">Two-Factor Authentication</Heading>
              <Label>What's your phone number ?</Label>
              <div className="my-3">
                <Input
                  value={userPhone}
                  placeholder="33601020304"
                  maxLength={11}
                  onChange={(e) =>
                    setUserPhone(e.target.value.replace(/[^0-9]/g, ""))
                  }
                  onKeyDown={(e) =>
                    e.key === "Enter" &&
                    userPhone.length > 0 &&
                    handlePhoneSubmit()
                  }
                  autoComplete="off"
                />
              </div>
              <div className="w-full text-right">
                <Link
                  className="hover:cursor-pointer hover:underline text-gray-500 hover:text-gray-700"
                  onClick={handlePhoneSubmit}
                  disabled={userPhone === "" || userPhone.length < 8}
                >
                  Next
                </Link>
              </div>
            </Field>
          ) : (
            <Field className="w-1/4 min-w-96">
              <BackButton
                onClick={
                  !isVerified
                    ? () => setIsUserPhoneSet(false)
                    : handleBackToLogin
                }
                text={!isVerified ? "Back" : "Back to login"}
              />
              <Heading className="text-xl">Two-Factor Authentication</Heading>
              <Label>
                {isFirstCodeSent
                  ? "You have received"
                  : "Click below to receive"}{" "}
                a 6-digit code on WhatsApp
              </Label>

              <CodeInput
                code={code}
                setCode={setCode}
                inputs={inputs}
                isCodeCorrect={isCodeCorrect}
                handleKeyDown={handleKeyDown}
              />

              <div className="flex justify-between items-center">
                <Link
                  className="hover:cursor-pointer hover:underline text-gray-500 hover:text-gray-700"
                  onClick={handleResendCode}
                >
                  {isFirstCodeSent ? "Resend code" : "Send code"}
                </Link>
                <Button
                  color="primaryButton"
                  loading={isLoading}
                  disabled={!code.join("") || code.join("").length < 6}
                  onClick={handleLogin}
                >
                  Log in
                </Button>
              </div>
            </Field>
          )}
        </div>
      </DialogBody>
    </Dialog>
  );
});

WhatsappTwoFactorAuthentication.displayName = "WhatsappTwoFactorAuthentication";
